import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import { isPathwaysXDP } from 'bundles/unified-common/utils/showPathways';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';

/** Returns true if the user is opted in to test variant (also used for checks within
 * *RootContent files to conditionally reorganize modules on the page). */
export const isStreamlinedXDP = () => {
  return GrowthDiscoveryEpicClient.get('useStreamlinedMWebXDP');
};

/** For general/desktop-inclusive changes, this can act as a standalone check. */
export const useStreamlinedXDP = () => {
  const { marketingSource, productSlug, hasDubbedLanguages } = usePageInfo();
  // marketingSource is undefined for Paid Ads and Google XDPs
  // productSlug is undefined non-XDP pages (e.g. LIHP)
  // isPathwaysXDP is true for slugs in the Pathways experiment; we want to exclude these
  // hasDubbedLanguages is true for XDPs with AI dubbing offered; we want to exclude these
  const isRegularXDP = !marketingSource && !!productSlug && !isPathwaysXDP(productSlug) && !hasDubbedLanguages;

  return isRegularXDP && isStreamlinedXDP();
};

/** For changes specific to mobile + tablet (<1024px), use this hook IF module is
 * below the fold (if above, use useStreamlinedXDP in conjunction with <Media> or
 * <Hidden> blocks). */
export const useMWebStreamlinedXDP = () => {
  const isTestingStreamlinedXDP = useStreamlinedXDP();
  const isMWeb = useMediaQuery(breakpoints.down('sm'));

  return isTestingStreamlinedXDP && isMWeb;
};

/** For changes specific to mobile (<600px / NOT tablet), use this hook IF module is
 * below the fold (if above, use useStreamlinedXDP in conjunction with <Media> or
 * <Hidden> blocks). */
export const useMobileOnlyStreamlinedXDP = () => {
  const isTestingStreamlinedXDP = useStreamlinedXDP();
  const isMobileDeviceWidth = useMediaQuery(breakpoints.down('xs'));

  return isTestingStreamlinedXDP && isMobileDeviceWidth;
};

interface Module {
  name: string;
}

/** Given an array of modules, returns a "sorted" array of the same type, where ThinBanner
 * appears below the Testimonials module. */
export const applyStreamlinedXDPModuleLayout = <T extends Module>(modules: T[]): T[] => {
  const copiedModules = [...modules];
  const thinBannerIndex = copiedModules.findIndex((module) => module.name === ModuleNames.ThinBanner);
  if (thinBannerIndex !== -1) {
    copiedModules.splice(thinBannerIndex, 1);
    const testimonialsIndex = copiedModules.findIndex((module) => module.name === ModuleNames.Testimonials);
    if (testimonialsIndex !== -1) {
      copiedModules.splice(testimonialsIndex + 1, 0, { name: ModuleNames.ThinBanner } as T);
    } else {
      copiedModules.splice(thinBannerIndex, 0, { name: ModuleNames.ThinBanner } as T);
    }
  }

  return copiedModules;
};
